import { useEffect, useRef, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  useAlimtalkTemplateListQuery,
  useAlimtalkTemplateDetailQuery,
  useUploadCsvMutation,
  useCsvSampleMutation,
  usePreviewAlimtalkSendMutation,
  useCreateAlimtalkSendMutation,
  useTestAlimtalkSendMutation,
} from '../queries/alimtalk';
import { deriveFieldRequirements } from './deriveFieldRequirements';
import {
  ALIMTALK_VALIDATION_MSG,
  buildCreateAlimtalkSendDto,
  buildTestAlimtalkSendDto,
  validateAlimtalkSendForm,
} from './alimtalkSendDto';
import { extractErrorMessage } from '../../lib/utils/checkErrorResponse';
import toastMsg from '../../lib/toastMsg';
import dayjs from '../../lib/dayjs';
import { AlimtalkPreviewState, AlimtalkSendFormValues, FieldRequirements } from '../../types/alimtalk/form';
import { CreateAlimtalkSendDto } from '../../types/alimtalk/send';
import { AlimtalkTemplate, AlimtalkTemplateList } from '../../types/alimtalk/template';

const INITIAL_FORM_VALUES: AlimtalkSendFormValues = {
  templateId: null,
  csvS3Key: null,
  csvFileName: null,
  memo: '',
  scheduledAt: null,
  couponList: [],
  alimtalkCouponId: null,
  previewCouponName: null,
  previewStartDate: null,
  previewEndDate: null,
  eventId: null,
  couponStartDate: null,
  couponEndDate: null,
};

export type UseAlimtalkSendFormReturn = {
  methods: UseFormReturn<AlimtalkSendFormValues>;
  templateList: AlimtalkTemplateList;
  isTemplateListLoading: boolean;
  selectedTemplate?: AlimtalkTemplate;
  requirements: FieldRequirements | null;
  /** 카카오 미리보기 {{변수}} → 운영자가 선택한 실제 값 (없으면 미리보기가 샘플로 대체) */
  previewVariables: Record<string, string>;
  /** 유형 상세 조회 실패 (에러 분기/재시도용) */
  isTemplateError: boolean;
  refetchTemplate: () => void;
  handleUploadCsv: (file: File) => void;
  isUploading: boolean;
  handleDownloadCsvSample: () => void;
  isSampleDownloading: boolean;
  /** 등록 1단계 — 검증 후 preview(dry-run) 호출, 결과를 확인 모달로 노출 */
  handleRegister: () => void;
  isPreviewLoading: boolean;
  /** null이면 확인 모달 닫힘 */
  previewState: AlimtalkPreviewState | null;
  /** preview 실패 시 동일 dto로 재시도 (읽기 전용 dry-run이라 안전) */
  handleRetryPreview: () => void;
  /** 등록 2단계 — 확인 모달의 [등록] 클릭 시 preview한 dto 그대로 등록 */
  handleConfirmRegister: () => void;
  closePreview: () => void;
  /** 테스트 발송 모달 열기 전 사전 검증 — 실패 시 사유 토스트 후 false (전화번호 입력 후 실패보다 나은 UX) */
  validateBeforeTestSend: () => boolean;
  /** 검증 통과 후 발송 시작하면 true, 검증 실패로 중단되면 false */
  handleTestSend: (testPhone: string) => boolean;
  isRegistering: boolean;
  isTesting: boolean;
};

export const useAlimtalkSendForm = (): UseAlimtalkSendFormReturn => {
  const methods = useForm<AlimtalkSendFormValues>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_VALUES,
  });

  const templateId = methods.watch('templateId');

  const { data: templateList = [], isLoading: isTemplateListLoading } = useAlimtalkTemplateListQuery({
    activeOnly: true,
  });
  const templateDetailQuery = useAlimtalkTemplateDetailQuery(templateId);
  const selectedTemplate = templateDetailQuery.data;
  const uploadCsvMutation = useUploadCsvMutation();
  const csvSampleMutation = useCsvSampleMutation();
  const previewMutation = usePreviewAlimtalkSendMutation();
  const createMutation = useCreateAlimtalkSendMutation();
  const testMutation = useTestAlimtalkSendMutation();

  const [previewState, setPreviewState] = useState<AlimtalkPreviewState | null>(null);

  const requirements = selectedTemplate ? deriveFieldRequirements(selectedTemplate) : null;

  // 미리보기 변수 매핑 — display 전용 preview* 필드만 읽는다(제출용 couponStartDate/End와 분리).
  // 변수 어휘는 백엔드 ACTION_TYPE_SUPPORTED_VARS 미러: couponEndDate(종료일), discountPeriod(시작~종료 조합).
  // 발급: 섹션이 운영자 입력을 preview*에 복사 / 만료: 최우선 쿠폰의 fixedDate 유효기간을 복사.
  // 채워지지 않은 변수(userName·discountText 등)는 KakaoPreview가 placeholder·샘플로 대체.
  const previewCouponName = methods.watch('previewCouponName');
  const previewStartDate = methods.watch('previewStartDate');
  const previewEndDate = methods.watch('previewEndDate');
  const previewEventId = methods.watch('eventId');
  const previewAlimtalkCouponId = methods.watch('alimtalkCouponId');
  const validStartDate =
    previewStartDate && dayjs(previewStartDate).isValid() ? dayjs(previewStartDate).format('YYYY-MM-DD') : null;
  const validEndDate =
    previewEndDate && dayjs(previewEndDate).isValid() ? dayjs(previewEndDate).format('YYYY-MM-DD') : null;
  const previewVariables: Record<string, string> = {
    ...(previewCouponName
      ? {
          couponName: previewCouponName,
          couponNameWithoutSingleQuote: previewCouponName.replace(/'/g, ''),
        }
      : {}),
    ...(validEndDate
      ? {
          couponEndDate: validEndDate,
          // 워커의 discountPeriod 조합 로직 미러 — 시작일 없으면 종료일만
          discountPeriod: validStartDate ? `${validStartDate} ~ ${validEndDate}` : validEndDate,
        }
      : {}),
    // 지원 어휘의 id류 변수도 선택값으로 치환 (본문에 노출되는 템플릿 대비 — 워커는 String(id)로 채움)
    ...(previewEventId ? { eventId: String(previewEventId) } : {}),
    ...(previewAlimtalkCouponId ? { couponId: String(previewAlimtalkCouponId) } : {}),
  };

  // preview 요청 세대 — 모달 닫힘/등록 완료/유형 변경 후 도착하는 늦은(in-flight) 응답이 stale dto로 모달을 되살리지 못하게 한다.
  // (되살아난 모달에서 이미 등록된 dto를 다시 [등록]하면 동일 대량 발송이 중복 등록되는 사고 경로)
  const previewGenerationRef = useRef(0);
  // [등록] 더블클릭 방지 — createMutation.isLoading은 같은 tick의 연속 클릭에 아직 반영되지 않으므로 ref로 즉시 잠근다
  const isConfirmSubmittingRef = useRef(false);

  // 유형 변경 시 조건부 필드 초기화 — 이전 유형 값이 DTO에 새지 않게 한다.
  // (memo/예약은 유형과 무관하므로 유지. CSV는 아래 형식 호환성 효과가 별도 판단)
  useEffect(() => {
    methods.setValue('couponList', []);
    methods.setValue('alimtalkCouponId', null);
    methods.setValue('previewCouponName', null);
    methods.setValue('previewStartDate', null);
    methods.setValue('previewEndDate', null);
    methods.setValue('eventId', null);
    methods.setValue('couponStartDate', null);
    methods.setValue('couponEndDate', null);
    // 진행 중이던 preview도 무효화 — 이전 유형의 dto로 확인 모달이 열리는 것 방지
    previewGenerationRef.current += 1;
    setPreviewState(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // 진행 중 업로드를 취소할 수 있도록 컨트롤러를 보관 — 늦게 끝난 업로드가 csvS3Key를 되살리는 race를 막는다
  const csvUploadAbortRef = useRef<AbortController | null>(null);

  // CSV 파일 형식은 템플릿에 종속(§6.2.2 — ACTION: 헤더 없는 userId 1열 / CSV: userId+변수 컬럼 헤더).
  // 형식이 같은 전환(ACTION↔ACTION)은 업로드를 유지해 재업로드를 강요하지 않고,
  // 형식이 달라지는 전환(모드 변경, CSV 모드 간 변수 구성 변경)만 초기화해 무효 파일이 조용히 남지 않게 한다.
  const lastCsvFormatRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTemplate) return;
    const formatKey =
      selectedTemplate.mode === 'CSV' ? `CSV:${[...selectedTemplate.variables].sort().join(',')}` : 'ACTION';
    if (lastCsvFormatRef.current !== null && lastCsvFormatRef.current !== formatKey) {
      // 진행 중 업로드도 취소 — 늦게 끝난 업로드가 새 형식과 맞지 않는 csvS3Key를 되살리는 것 방지
      csvUploadAbortRef.current?.abort();
      methods.setValue('csvS3Key', null);
      methods.setValue('csvFileName', null);
    }
    lastCsvFormatRef.current = formatKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  // 실패·취소 토스트는 useUploadCsvMutation.onError가 담당 → mutate(콜백)로 부동 프로미스(취소 시 reject) 방지
  const handleUploadCsv = (file: File): void => {
    // 재업로드 시 직전 업로드는 결과가 필요 없으므로 취소
    csvUploadAbortRef.current?.abort();
    const controller = new AbortController();
    csvUploadAbortRef.current = controller;

    uploadCsvMutation.mutate(
      { file, signal: controller.signal },
      {
        // 성공한 파일만 기록 (실패 시 직전 업로드 유지) — 새 파일 업로드 시 csvS3Key/파일명 모두 대체
        onSuccess: (csvS3Key) => {
          methods.setValue('csvS3Key', csvS3Key);
          methods.setValue('csvFileName', file.name);
        },
      },
    );
  };

  // 샘플 CSV — 백엔드는 헤더 문자열({csvHeader})만 주고 파일은 클라이언트 Blob으로 생성.
  // CSV 모드 전용(그 외 백엔드 400)이라 needsCsvVariables 아니면 호출 자체를 막는다.
  const handleDownloadCsvSample = (): void => {
    if (templateId == null || !requirements?.needsCsvVariables) return;
    csvSampleMutation.mutate(templateId, {
      onSuccess: ({ csvHeader }) => {
        if (!csvHeader) return;
        // UTF-8 BOM(U+FEFF) — 한글 변수 헤더의 Excel 모지바케 방지 (보이지 않는 리터럴 대신 이스케이프로 명시)
        const blob = new Blob(['\uFEFF' + csvHeader], { type: 'text/csv' });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `alimtalk-sample-${templateId}.csv`;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // 즉시 revoke하면 일부 브라우저(Safari 등)에서 다운로드가 blob을 읽기 전에 무효화될 수 있어 지연
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      },
    });
  };

  // preview(dry-run)를 호출해 결과/실패를 확인 모달 상태로 반영. 실패해도 모달을 열어 등록을 막지 않는다.
  // isCsvMode는 dto와 같은 시점에 동결 — 응답 도착 전에 유형이 바뀌어도 모달 표시 방식이 dto와 어긋나지 않게.
  const requestPreview = (dto: CreateAlimtalkSendDto, isCsvMode: boolean): void => {
    const generation = ++previewGenerationRef.current;
    previewMutation.mutate(dto, {
      onSuccess: (result) => {
        if (generation !== previewGenerationRef.current) return; // 닫힘/등록/유형 변경으로 무효화된 늦은 응답
        setPreviewState({ dto, isCsvMode, result, errorMessage: null });
      },
      onError: (error) => {
        if (generation !== previewGenerationRef.current) return;
        setPreviewState({
          dto,
          isCsvMode,
          result: null,
          errorMessage: extractErrorMessage(error, '발송 미리보기를 불러오지 못했습니다.'),
        });
      },
    });
  };

  // 등록 1단계 — 검증 통과 시 preview 호출(백엔드 권장: preview 확인 후 등록)
  const handleRegister = (): void => {
    const values = methods.getValues();
    const errorMessage = validateAlimtalkSendForm(values, requirements);
    if (errorMessage || !requirements) {
      toastMsg.error(errorMessage ?? ALIMTALK_VALIDATION_MSG.TEMPLATE_REQUIRED);
      return;
    }
    requestPreview(buildCreateAlimtalkSendDto(values, requirements), requirements.needsCsvVariables);
  };

  const handleRetryPreview = (): void => {
    if (previewState) {
      requestPreview(previewState.dto, previewState.isCsvMode);
    }
  };

  // 등록 2단계 — 확인 모달에서 [등록] 클릭. preview한 dto 그대로 제출해 확인 내용과 등록 내용을 일치시킨다.
  const handleConfirmRegister = (): void => {
    if (!previewState || isConfirmSubmittingRef.current) return;
    // 모달을 열어두고 고민하는 사이 예약 시각이 과거가 되면 백엔드는 즉시 발송해버린다 — frozen dto 기준 재검증
    if (previewState.dto.scheduledAt && dayjs(previewState.dto.scheduledAt).isBefore(dayjs())) {
      toastMsg.error(ALIMTALK_VALIDATION_MSG.SCHEDULED_PAST);
      return;
    }
    isConfirmSubmittingRef.current = true;
    createMutation.mutate(previewState.dto, {
      // 성공 후 폼 초기화로 중복 제출 방지 (Phase 2 이력 페이지 생기면 리다이렉트로 대체)
      onSuccess: () => {
        previewGenerationRef.current += 1; // in-flight preview 재시도 응답 무효화
        setPreviewState(null);
        methods.reset(INITIAL_FORM_VALUES);
      },
      onSettled: () => {
        isConfirmSubmittingRef.current = false;
      },
    });
  };

  // 백엔드 TestAlimtalkSendDto가 Create를 상속(CSV·메모 포함 동일 검증) → 테스트도 등록과 같은 전체 검증
  const validateBeforeTestSend = (): boolean => {
    const errorMessage = validateAlimtalkSendForm(methods.getValues(), requirements);
    if (errorMessage) {
      toastMsg.error(errorMessage);
      return false;
    }
    return true;
  };

  const handleTestSend = (testPhone: string): boolean => {
    if (!validateBeforeTestSend() || !requirements) return false;
    testMutation.mutate(buildTestAlimtalkSendDto(methods.getValues(), requirements, testPhone));
    return true;
  };

  return {
    methods,
    templateList,
    isTemplateListLoading,
    selectedTemplate,
    requirements,
    previewVariables,
    isTemplateError: templateDetailQuery.isError,
    refetchTemplate: () => {
      templateDetailQuery.refetch();
    },
    handleUploadCsv,
    isUploading: uploadCsvMutation.isLoading,
    handleDownloadCsvSample,
    isSampleDownloading: csvSampleMutation.isLoading,
    handleRegister,
    isPreviewLoading: previewMutation.isLoading,
    previewState,
    handleRetryPreview,
    handleConfirmRegister,
    // 등록 요청 진행 중엔 닫기(취소/X/ESC/mask) 무시 — 닫힌 뒤 폼을 새로 작성하다 늦은 onSuccess reset에 날아가는 사고 방지
    closePreview: () => {
      if (!createMutation.isLoading) {
        previewGenerationRef.current += 1; // in-flight preview(재시도 포함) 응답이 닫힌 모달을 되살리지 못하게 무효화
        setPreviewState(null);
      }
    },
    validateBeforeTestSend,
    handleTestSend,
    isRegistering: createMutation.isLoading,
    isTesting: testMutation.isLoading,
  };
};
